/**
 * useDraftingListPubSub - keeps the drafting LIST live.
 *
 * Joins the PubSub group of every in-flight (queued/processing) item and
 * dispatches applyPubSubEvent so their status/progress update in place, then
 * refetches the collection once when an item finishes (to pick up final
 * word_count etc.). Mirrors useDraftingPubSub, but multiplexed across items.
 *
 * Idempotent joins (tracked in a ref); leaves everything on unmount.
 */

import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector, type RootState } from '../store';
import { getMultiHubSocketService } from '../utils/MultiHubSocketService';
import {
  applyPubSubEvent,
  fetchDraftingList,
  type DraftingItem,
  type DraftingKind,
} from '../store/slices/draftingSlice';

const HUB = 'ai_processing';

/**
 * @param items the combined list (may mix kinds); each item's own `kind`
 *   is used to refetch the right collection when it finishes.
 */
export function useDraftingListPubSub(items: DraftingItem[]): void {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((s: RootState) => s.user?.userInfo?.UserID);

  const joinedRef = useRef<Set<string>>(new Set());
  const handlerRef = useRef<((...args: any[]) => void) | null>(null);
  // Live refs so the once-registered handler always sees current values.
  const inflightCorrRef = useRef<Set<string>>(new Set());
  const corrToKindRef = useRef<Record<string, DraftingKind>>({});

  useEffect(() => {
    const hub = getMultiHubSocketService();
    if (userId) hub.setCurrentUserId(String(userId));

    const inflight = items.filter(
      (i) => (i.status === 'processing' || i.status === 'queued') && i.pubsub?.group,
    );
    inflightCorrRef.current = new Set(
      inflight.map((i) => i.correlation_id || i.uuid).filter(Boolean) as string[],
    );
    corrToKindRef.current = inflight.reduce((acc, i) => {
      const c = i.correlation_id || i.uuid;
      if (c) acc[c] = i.kind;
      return acc;
    }, {} as Record<string, DraftingKind>);

    // Register the multiplexed handler exactly once.
    if (!handlerRef.current) {
      const handler = (data: any) => {
        const ev = Array.isArray(data) ? data[0] : (data || {});
        const nested = ev.data || {};
        const corr = ev.correlationId || ev.correlation_id || ev.CorrelationID;
        if (!corr || !inflightCorrRef.current.has(corr)) return;

        const status = (ev.status || nested.status || '').toLowerCase();
        const sub_type = ev.sub_type || nested.sub_type || ev.event_name || '';
        const message = ev.message || ev.stepName || '';
        const progress_percent = ev.progressPercent ?? ev.progress_percent ?? ev.progress ?? null;

        dispatch(applyPubSubEvent({ correlationId: corr, status, sub_type, progress_percent, message }));

        const terminal = status === 'completed' || status === 'failed'
          || sub_type === 'draft_completed' || sub_type === 'draft_failed';
        if (terminal) {
          const finishedKind = corrToKindRef.current[corr];
          inflightCorrRef.current.delete(corr);
          // Pick up final word_count / metadata for the finished item's kind.
          if (finishedKind) dispatch(fetchDraftingList({ kind: finishedKind }));
        }
      };
      handlerRef.current = handler;
      hub.on(HUB, '*', handler);
    }

    // Connect + join any newly in-flight groups.
    (async () => {
      await hub.connectToHub(HUB);
      inflight.forEach((i) => {
        const g = i.pubsub!.group;
        if (!joinedRef.current.has(g)) {
          hub.emit(HUB, 'join', { group: g });
          joinedRef.current.add(g);
        }
      });
    })();
  }, [dispatch, items, userId]);

  // Tear down on unmount.
  useEffect(() => () => {
    const hub = getMultiHubSocketService();
    if (handlerRef.current) hub.off(HUB, '*', handlerRef.current);
    joinedRef.current.forEach((g) => hub.emit(HUB, 'leave', { group: g }));
    joinedRef.current.clear();
    handlerRef.current = null;
  }, []);
}
